'use client';

import { useMemo, useState } from 'react';
import Slider from '@/components/ui/Slider';
import FlowResistivityField from '@/components/ui/FlowResistivityField';
import {
  type Units,
  panelUnitLabel,
  toPanelDisplay,
  fromPanelDisplay,
} from '@roomtreatment/core';
import {
  absorptionCurve,
  nrc,
  ABS_F_MIN,
  ABS_F_MAX,
  type AbsorberSpec,
} from '@roomtreatment/floorplan-core';
import { EXPLANATION, SECOND_LINE } from '@/components/seo/roles';
import { optionChipClass } from '@/components/room/chrome/selection';
import ToolLeadIn from '@/components/tools/ToolLeadIn';
import AbsorptionCanvas from './AbsorptionCanvas';

const THICKNESS_MIN_M = 0.01;
const THICKNESS_MAX_M = 0.15;
const AIR_GAP_MIN_M = 0;
const AIR_GAP_MAX_M = 0.2;

const ACCENT = 'var(--rt-accent)';

function UnitsToggle({ units, onChange }: { units: Units; onChange: (u: Units) => void }) {
  return (
    <div
      className="inline-flex items-center gap-1 rounded-lg border border-[var(--app-border)] p-0.5"
      role="group"
      aria-label="Unit system"
      data-testid="porous-absorber-units-toggle"
    >
      {(['metric', 'imperial'] as const).map((u) => (
        <button
          key={u}
          type="button"
          onClick={() => onChange(u)}
          aria-pressed={units === u}
          // SELECTION IS NOT AN ACTION: raised when chosen, never accent-filled
          // (AGENTS.md Button + selection rule; selection.ts).
          className={optionChipClass(units === u)}
        >
          {u === 'metric' ? 'cm' : 'in'}
        </button>
      ))}
    </div>
  );
}

function PanelDimSlider({
  label,
  valueM,
  minM,
  maxM,
  units,
  onChangeM,
  testId,
}: {
  label: string;
  valueM: number;
  minM: number;
  maxM: number;
  units: Units;
  onChangeM: (m: number) => void;
  testId: string;
}) {
  const unitLabel = panelUnitLabel(units);
  return (
    <Slider
      label={label}
      min={minM}
      max={maxM}
      step={(maxM - minM) / 200}
      color={ACCENT}
      thumbs={[
        {
          value: valueM,
          onChange: (m) => onChangeM(Math.max(minM, Math.min(maxM, m))),
          ariaLabel: `${label} value`,
          testId: `${testId}-thumb`,
          format: (m) => String(Math.round(toPanelDisplay(m, units) * 10) / 10),
          parse: (raw) => {
            const n = parseFloat(raw.replace(',', '.'));
            return Number.isFinite(n) ? fromPanelDisplay(n, units) : null;
          },
        },
      ]}
      renderReadout={(thumbs) => {
        const t = thumbs[0]!;
        return (
          <span className="inline-flex items-baseline gap-1 font-mono text-body-sm font-bold leading-none text-[var(--rt-accent)]">
            <span data-testid={testId} className="leading-none">{Math.round(toPanelDisplay(t.value, units) * 10) / 10}</span>
            <span className="text-body-sm leading-none text-[var(--rt-accent)]">{unitLabel}</span>
          </span>
        );
      }}
    />
  );
}

export default function PorousAbsorberWidget() {
  const [units, setUnits] = useState<Units>('metric');
  const [thicknessM, setThicknessM] = useState(0.05);
  const [airGapM, setAirGapM] = useState(0);
  const [sigma, setSigma] = useState(17000);
  const [randomIncidence, setRandomIncidence] = useState(true);

  const spec: AbsorberSpec = useMemo(
    () => ({ sigma, thickness: thicknessM, airGap: airGapM }),
    [sigma, thicknessM, airGapM],
  );

  const incidence = randomIncidence ? 'random' : 'normal';
  const curve = useMemo(
    () => (thicknessM > 0 ? absorptionCurve(spec, 140, incidence) : null),
    [spec, thicknessM, incidence],
  );
  const rating = useMemo(() => {
    if (thicknessM <= 0) return 0;
    return nrc(spec, incidence);
  }, [spec, thicknessM, incidence]);

  return (
    <div className="space-y-6" data-testid="porous-absorber-widget">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className={EXPLANATION}>
          Delany-Bazley-Miki porous absorber model — flow resistivity, thickness, and an
          optional air gap behind the material.
        </p>
        <UnitsToggle units={units} onChange={setUnits} />
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 pb-6 border-b border-[var(--app-border)]">
        <PanelDimSlider
          label="Absorber thickness"
          valueM={thicknessM}
          minM={THICKNESS_MIN_M}
          maxM={THICKNESS_MAX_M}
          units={units}
          onChangeM={setThicknessM}
          testId="porous-absorber-thickness"
        />
        <PanelDimSlider
          label="Air gap behind absorber"
          valueM={airGapM}
          minM={AIR_GAP_MIN_M}
          maxM={AIR_GAP_MAX_M}
          units={units}
          onChangeM={setAirGapM}
          testId="porous-absorber-airgap"
        />
      </div>

      <FlowResistivityField
        sigma={sigma}
        onChange={setSigma}
        label={
          <span className="text-caption font-semibold text-[var(--rt-ink)]">
            Material · flow resistivity σ
          </span>
        }
        unitLabel="Pa·s/m²"
        color={ACCENT}
        testIdPrefix="porous-absorber"
        captionClassName="text-caption text-[var(--rt-ink-dim)]"
        inputClassName="w-[80px] rounded border border-[var(--app-border)] bg-[var(--app-bg)] px-2 py-1 text-right text-caption tabular-nums text-[var(--rt-ink)]"
        // Routed through the shared selection chip (S2: raised, never an
        // accent fill) — it also happens to clear the 24px AA tap-target
        // floor these presets measured under before (S9).
        presetClassName={(active) => optionChipClass(active)}
      />

      <label className="flex items-center gap-2 text-caption text-[var(--rt-ink)]">
        <input
          type="checkbox"
          checked={randomIncidence}
          onChange={(e) => setRandomIncidence(e.target.checked)}
          data-testid="porous-absorber-random-incidence"
        />
        Random (field) incidence
        <span className={SECOND_LINE}>
          — diffuse-field average via Paris&apos; formula, instead of straight-on
        </span>
      </label>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <span className="text-caption font-semibold text-[var(--rt-ink)]">Absorption vs. frequency</span>
          <span className="text-caption text-[var(--rt-ink-dim)]" data-testid="porous-absorber-nrc">
            NRC ≈ {rating.toFixed(2)}
          </span>
        </div>
        {curve ? (
          <AbsorptionCanvas
            freqs={curve.freqs}
            alpha={curve.alpha}
            fMin={ABS_F_MIN}
            fMax={ABS_F_MAX}
          />
        ) : (
          <p className={SECOND_LINE}>Set a thickness greater than zero.</p>
        )}
      </div>

      <ToolLeadIn
        result={`NRC ≈ ${rating.toFixed(2)} for this absorber build.`}
        ctaLabel="Design a custom panel in 3D"
      />
    </div>
  );
}
